import {intersection} from 'lodash';
import {AstView, EnrichedAstNode, RenderVariantsType} from '../types';
import {visitor, visitorPostOrder} from '../helpers/AstHelpers';

export function findNode(ast: EnrichedAstNode, fn: (ast: EnrichedAstNode) => boolean): EnrichedAstNode | undefined {
    if (fn(ast)) {
        return ast;
    }
    for (const child of ast.children) {
        const result = findNode(child, fn);
        if (result) {
            return result;
        }
    }
    return undefined;
}

export function findNodeById(ast: EnrichedAstNode, id: string): EnrichedAstNode | undefined {
    if (ast.internalId === id) {
        return ast;
    }
    for (const child of ast.children) {
        const result = findNodeById(child, id);
        if (result) {
            return result;
        }
    }
    return undefined;
}

export function findNextNodeDFS(tree: EnrichedAstNode, id: string): EnrichedAstNode | undefined {
    const current = findNodeById(tree, id);

    if (!current) {
        // bad input data
        throw new Error('can not find node');
    }

    if (current.children.length > 0) {
        return current.children[0];
    }

    return findSibling(tree, id);
}

function findSibling(tree: EnrichedAstNode, id: string): undefined | EnrichedAstNode {
    const parent = findParentOfNodeById(tree, id);
    if (!parent) {
        // is root or maybe error case
        return undefined;
    }

    const childIndex = parent.children.findIndex((node) => node.internalId === id);
    if (childIndex === -1) {
        // bad input data
        throw new Error('can not find node');
    }

    if (childIndex < parent.children.length - 1) {
        // go to sibling
        return parent.children[childIndex + 1];
    }

    return findSibling(tree, parent.internalId);
}

export function getComponentFromInstance(view: AstView, id: string) {
    const instance = findNodeById(view.tree, id);
    if (!instance) {
        throw new Error('Can not find');
    }
    if (instance.type !== 'Instance') {
        throw new Error('Not an instance');
    }

    return (view.meta.complexComponentMap[instance.refId].renderVariants as RenderVariantsType).finalTree;
}

// Instance helper
export function getWorkingNode(node: EnrichedAstNode, view: AstView) {
    return node.type === 'Instance' ? getComponentFromInstance(view, node.internalId) : node;
}

export function findAllInstancesByComponentId(tree: EnrichedAstNode, refId: string): EnrichedAstNode[] {
    const result: EnrichedAstNode[] = [];

    if (tree.type === 'Instance' && tree.refId === refId) {
        result.push(tree);
    }
    tree.children.forEach((child) => {
        result.push(...findAllInstancesByComponentId(child, refId));
    });

    return result;
}

export function findParentOfNodeById(ast: EnrichedAstNode, id: string): EnrichedAstNode | undefined {
    for (const child of ast.children) {
        if (child.internalId === id) {
            return ast;
        }
    }

    for (const child of ast.children) {
        const result = findParentOfNodeById(child, id);
        if (result) {
            return result;
        }
    }
    return;
}

/**
 * @description When we're converting units from to `em` we need to look up the tree to find the
 * `font-size` that the element inherits from. If the element itself has a `font-size` then it will
 * be used for the `em` units, if not we can just climb up the tree until we do find one. If we do
 * not find one then it will be the 1default `16px` that the browser uses (or whatever `font-size`
 * is set on the body, but we won't hanlde cases where the body font size is changed because that
 * lives outside the scope of our view context).
 */
export const findClosestParentWithFontSize = (view: EnrichedAstNode, id: string): EnrichedAstNode | null => {
    const parent = findParentOfNodeById(view, id);

    if (!parent) {
        return null;
    }

    if (parent.styles['fontSize']) {
        return parent;
    }

    return findClosestParentWithFontSize(view, parent.internalId);
};

export const findAncestors = (nodeOfInterest: EnrichedAstNode, root: EnrichedAstNode) => {
    const parentIds: string[] = [];

    let current = nodeOfInterest;

    while (current !== root) {
        const parent = findParentOfNodeById(root, current.internalId);
        if (parent) {
            parentIds.push(parent.internalId);
        } else {
            // likely something went wrong.
            break;
        }
        current = parent;
    }
    return parentIds;
};

export const isNodeAncestor = (view: EnrichedAstNode, nodeId: string, nodeOfInterestId: string) => {
    const node = findNodeById(view, nodeId);

    if (!node) {
        return false;
    }

    const ancestorIds = findAncestors(node, view);

    return ancestorIds.includes(nodeOfInterestId);
};

export const areNodesAncestors = (
    view: EnrichedAstNode,
    nodeId: string,
    nodesOfInterestIds: Array<string>
): Array<string> => {
    const node = findNodeById(view, nodeId);

    if (!node) {
        return [];
    }

    const foundAncestorIds = findAncestors(node, view);

    return intersection(foundAncestorIds, nodesOfInterestIds);
};

type GetNonSystemGeneratedChildNodes = {
    (node: EnrichedAstNode): EnrichedAstNode[];
    <R>(node: EnrichedAstNode, mapFunc: (n: EnrichedAstNode) => R): R[];
};

export const getNonSystemGeneratedChildNodes: GetNonSystemGeneratedChildNodes = (
    node,
    mapFunc = (n: EnrichedAstNode) => n
) => {
    const nonSystemGeneratedChildNodes = node.children.filter((n) => !n.meta.systemGenerated);

    return nonSystemGeneratedChildNodes.map(mapFunc);
};

type GetMatchingChildNodes = {
    (
        node: EnrichedAstNode,
        conditionFunction: (node: EnrichedAstNode) => boolean,
        mapFunc?: undefined
    ): EnrichedAstNode[];
    <R>(
        node: EnrichedAstNode,
        conditionFunction: (node: EnrichedAstNode) => boolean,
        mapFunc?: (n: EnrichedAstNode) => R
    ): R[];
};

export const getChildrenMatchingCondition: GetMatchingChildNodes = (node, conditionFunction, mapFunc) => {
    const matchingNodes = node.children.filter(conditionFunction);

    if (mapFunc) {
        return matchingNodes.map(mapFunc);
    }

    return matchingNodes;
};

export const recursivelyGetAllChildrenMatchingCondition: GetMatchingChildNodes = (node, conditionFunction, mapFunc) => {
    const children: EnrichedAstNode[] = [];

    visitor(node, node.meta, (curr) => {
        const matchingNodes = curr.children.filter(conditionFunction);

        if (mapFunc) {
            return children.push(...(matchingNodes.map(mapFunc) as EnrichedAstNode[]));
        }

        return children.push(...matchingNodes);
    });

    return children;
};

export const findLowestSharedParent = (view: AstView, nodeIds: string[]) => {
    const nodeChildMap: {
        [id: string]: {
            nodeChildrenIds: string[];
            depth: number;
        };
    } = {};

    visitorPostOrder(view.tree, view.meta, (currentNode) => {
        const childList: string[] = currentNode.children.map((c) => c.internalId);

        currentNode.children.forEach((child) => {
            const childInfo = nodeChildMap[child.internalId];
            if (!childInfo) {
                throw new Error('function broken!!!');
            }
            childList.push(...childInfo.nodeChildrenIds);
        });

        const calcDepth =
            currentNode.children.length > 0 ? nodeChildMap[currentNode.children[0].internalId].depth + 1 : 0;

        nodeChildMap[currentNode.internalId] = {
            nodeChildrenIds: childList,
            depth: calcDepth,
        };
    });

    const candidatesWithAllChildren = Object.keys(nodeChildMap)
        .filter((key) => {
            const data = nodeChildMap[key];
            const intersectionResult = intersection(data.nodeChildrenIds, nodeIds);
            return intersectionResult.length === nodeIds.length;
        })
        .sort((a, b) => {
            const aDepth = nodeChildMap[a].depth;
            const bDepth = nodeChildMap[b].depth;
            return aDepth - bDepth;
        });

    return candidatesWithAllChildren[0];
};

export const getAllChildren = (node: EnrichedAstNode) => {
    const directChildren = node.children.map((c) => c.internalId);

    const childChildrenIds = node.children.map((child) => getAllChildren(child));

    childChildrenIds.forEach((list) => directChildren.push(...list));

    return directChildren;
};

export const findRootsOfSelection = (view: AstView, selectedNodeIds: string[]) => {
    // group
    const childNodes: EnrichedAstNode[] = selectedNodeIds
        .map((id) => findNodeById(view.tree, id))
        .filter((node) => !!node) as EnrichedAstNode[];

    let childrenToGroup = [...childNodes].filter((node) => !node.meta.systemGenerated);

    [...childrenToGroup].forEach((node) => {
        // get all children of this node.
        const nodeChildren: string[] = getAllChildren(node);
        // filter there children from childNodes list.
        childrenToGroup = childrenToGroup.filter((child) => {
            return !nodeChildren.includes(child.internalId);
        });
    });

    return childrenToGroup;
};
